import { useEffect, useMemo, useState } from "react";
import Card from "../components/Card";
import Button from "../components/Button";
import { loadSession } from "../auth";
import {
  listMyCertificates,
  listUserCertificates,
  listDirectReports,
  adminListUsers,
  certificateDownload, // NEW: use per-kind download SAS
} from "../services/api";
import { shouldBlockFeatureLoads } from "../utils/onboardingGate";
import { useTranslation } from "react-i18next";

function kindBadge(kind) {
  if (!kind) return null;
  const base = "px-2 py-0.5 rounded-full text-xs border";
  const cls =
    kind === "SSM"
      ? `${base} bg-blue-50 border-blue-200 text-blue-800`
      : `${base} bg-purple-50 border-purple-200 text-purple-800`;
  return <span className={cls}>{kind}</span>;
}

function pickBestPreviewHref(c) {
  // prefer signed → generated → legacy uploaded
  return (
    c.signedUrlSas ||
    c.signedUrl ||
    c.generatedUrlSas ||
    c.generatedUrl ||
    c.uploadedUrlSas ||
    c.uploadedUrl ||
    ""
  );
}

// TODO (demo only) – remove once backend is ready
const DEMO_TITLE_MAP = {
  "IIG": "Test IIG",
  "ILM": "Test ILM",
  "IP Trim 1 2026": "Test Anual 2026",
  "Anual 2026": "Test Anual 2026",
};
const DEMO_ORDER = [
  "Instruire IIG", "Instruire ILM", "Instruire Periodica Trim 1 2026",
  "Test IIG", "Test ILM", "Test Anual 2026",
  "Declaratie traseu"
];
function _demoRenameTitle(raw) {
  const key = Object.keys(DEMO_TITLE_MAP).find((k) => raw.toUpperCase() === k.toUpperCase());
  return key ? DEMO_TITLE_MAP[key] : raw;
}
function _demoMock(title, kind) {
  return { training: { title }, kind, _isMock: true };
}
function demoTransform(items, kind) {
  const renamed = items.map((c) => {
    const raw = c.training?.title || c.training?.slug || "";
    const next = _demoRenameTitle(raw);
    return next === raw ? c : { ...c, training: { ...(c.training || {}), title: next } };
  });
  const mocks = [
    ...(kind === "SSM" ? [_demoMock("Declaratie traseu", kind)] : []),
    _demoMock("Instruire IIG", kind),
    _demoMock("Instruire ILM", kind),
    _demoMock("Instruire Periodica Trim 1 2026", kind),
  ];
  return [...renamed, ...mocks].sort((a, b) => {
    const ta = a.training?.title || "";
    const tb = b.training?.title || "";
    const ia = DEMO_ORDER.findIndex((x) => ta.toUpperCase() === x.toUpperCase());
    const ib = DEMO_ORDER.findIndex((x) => tb.toUpperCase() === x.toUpperCase());
    return (ia === -1 ? DEMO_ORDER.length : ia) - (ib === -1 ? DEMO_ORDER.length : ib);
  });
}

function CertList({ items }) {
  const { t/*, i18n*/ } = useTranslation();
  if (!items?.length) {
    return (
      <div className="text-sm text-slate-500 py-4 text-center">
        {t("certificates.no_certificates")}
      </div>
    );
  }

  return (
    <ul className="divide-y divide-slate-100">
      {items.map((c, i) => {
        const href = pickBestPreviewHref(c);
        /*const when = c.createdAt
          ? new Date(c.createdAt).toLocaleString(i18n.language)
          : "—";*/
        // TODO (demo): rename handled by demoTransform before CertList
        const title = c.training?.title || c.training?.slug || t("certificates.training_fallback");

        return (
          <li key={i} className="py-3.5 flex items-center gap-3">
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2">
                <div className="font-semibold text-slate-900 truncate">{title}</div>
                {kindBadge(c.kind)}
              </div>
              {/* TODO (demo): issued_on date hidden
              <div className="text-xs text-slate-500">
                {t("certificates.issued_on", { when })}
              </div>
              */}
            </div>

            <div className="flex gap-2">
              

              <Button
                variant="ghost"
                onClick={async () => {
                  try {
                    // If backend provided kind + training slug, ask for a fresh SAS
                    if (c.kind && c.training?.slug) {
                      // Prefer signed; fall back to generated
                      let out;
                      try {
                        out = await certificateDownload(c.training.slug, {
                          kind: c.kind,
                          type: "signed",
                        });
                      } catch {
                        out = await certificateDownload(c.training.slug, {
                          kind: c.kind,
                          type: "generated",
                        });
                      }
                      const url = out?.url;
                      if (url) {
                        window.open(url, "_blank", "noopener");
                        return;
                      }
                    }
                    // fallback to whatever preview we have
                    if (href) {
                      window.open(href, "_blank", "noopener");
                    } else {
                      alert(t("certificates.error_no_url", "No certificate URL returned"));
                    }
                  } catch (e) {
                    alert(e?.message || t("certificates.error_generic", "Failed to download certificate"));
                  }
                }}
              >
                {t("certificate.btn_print")}
              </Button>
            </div>
          </li>
        );
      })}
    </ul>
  );
}

function UserPickerList({ items, selectedId, onPick, emptyLabel }) {
  const { t } = useTranslation();
  const label = emptyLabel || t("certificates.no_users");
  if (!items?.length) {
    return <div className="text-sm text-slate-500">{label}</div>;
  }
  return (
    <div className="max-h-[380px] overflow-auto space-y-0.5 pr-1">
      {items.map((u) => (
        <label
          key={u.id}
          className={`flex items-center gap-3 p-2.5 rounded-lg cursor-pointer transition-colors ${
            selectedId === u.id ? "bg-blue-50 border border-blue-200" : "hover:bg-slate-50 border border-transparent"
          }`}
        >
          <input
            type="radio"
            name="pickUser"
            checked={selectedId === u.id}
            onChange={() => onPick(u)}
          />
          <div className="flex-1">
            <div className="font-medium text-sm text-slate-900">{u.name || "—"}</div>
            <div className="text-xs text-slate-500">{u.email}</div>
          </div>
        </label>
      ))}
    </div>
  );
}

export default function Certificates() {
  const { t } = useTranslation();
  const user = loadSession()?.user;
  const isManager = !!user?.derived?.isManager;
  const isOrgAdmin =
    user?.derived?.isOrgAdmin ||
    (Array.isArray(user?.roles) && user.roles.includes("org_super_admin"));
  const isPlatformAdmin =
    user?.derived?.isPlatformAdmin ||
    (Array.isArray(user?.roles) && user.roles.includes("platform_admin"));

  //const [tab, setTab] = useState("mine"); // mine | team | tenant
  const canSeeTeam = isManager;
  const canSeeTenant = isOrgAdmin || isPlatformAdmin;

  // My certificates
  const [myLoading, setMyLoading] = useState(false);
  const [myItems, setMyItems] = useState([]);

  // Team (manager) search/pick
  const [reports, setReports] = useState([]);
  const [reportsLoading, setReportsLoading] = useState(false);
  const [qTeam, setQTeam] = useState("");
  const [pickedReport, setPickedReport] = useState(null);
  const [pickedReportCerts, setPickedReportCerts] = useState([]);
  const [pickedReportLoading, setPickedReportLoading] = useState(false);

  // Tenant (org admin / platform) search/pick
  const [tenantUsers, setTenantUsers] = useState([]);
  const [qTenant, setQTenant] = useState("");
  const [tenantLoading, setTenantLoading] = useState(false);
  const [pickedTenantUser, setPickedTenantUser] = useState(null);
  const [pickedTenantCerts, setPickedTenantCerts] = useState([]);
  const [pickedTenantLoading, setPickedTenantLoading] = useState(false);

  // Load my certs on mount or when tab switches to "mine"
  useEffect(() => {
    // Only load personal certificates when feature loads are allowed.
    let alive = true;
    (async () => {
      try {
        setMyLoading(true);
        if (shouldBlockFeatureLoads()) {
          setMyItems([]);
          return;
        }
        const items = await listMyCertificates({ sasMinutes: 60 });
        if (!alive) return;
        setMyItems(items);
      } finally {
        if (alive) setMyLoading(false);
      }
    })();
    return () => {
      alive = false;
    };
  }, [/*tab*/]);

  // Load direct reports for managers
  useEffect(() => {
    if (!canSeeTeam/* || tab !== "team"*/) return;
    let alive = true;
    (async () => {
      try {
        setReportsLoading(true);
        const items = await listDirectReports();
        if (!alive) return;
        setReports(items || []);
      } finally {
        if (alive) setReportsLoading(false);
      }
    })();
    return () => {
      alive = false;
    };
  }, [canSeeTeam/*, tab*/]);

  // Search tenant users (org/platform)
  useEffect(() => {
    if (!canSeeTenant/* || tab !== "tenant"*/) return;
    let alive = true;
    const doSearch = async () => {
      try {
        setTenantLoading(true);
        const { items } = await adminListUsers({
          q: qTenant || "",
          page: 1,
          pageSize: 50,
          isManager: "",
          includeDeactivated: false,
        });
        if (!alive) return;
        setTenantUsers(items || []);
      } finally {
        if (alive) setTenantLoading(false);
      }
    };
    const tmr = setTimeout(doSearch, 200);
    return () => {
      alive = false;
      clearTimeout(tmr);
    };
  }, [canSeeTenant, /*tab,*/ qTenant]);

  const filteredReports = useMemo(() => {
    const term = qTeam.trim().toLowerCase();
    if (!term) return reports;
    return reports.filter(
      (u) =>
        (u.name || "").toLowerCase().includes(term) ||
        (u.email || "").toLowerCase().includes(term)
    );
  }, [reports, qTeam]);

  async function loadReportCerts(u) {
    setPickedReport(u);
    setPickedReportCerts([]);
    if (!u) return;
    setPickedReportLoading(true);
    try {
      const items = await listUserCertificates(u.id, { sasMinutes: 60 });
      setPickedReportCerts(items);
    } catch (e) {
      alert(e?.message || t("certificates.error_generic", "Failed to load certificates"));
    } finally {
      setPickedReportLoading(false);
    }
  }

  async function loadTenantUserCerts(u) {
    setPickedTenantUser(u);
    setPickedTenantCerts([]);
    if (!u) return;
    setPickedTenantLoading(true);
    try {
      const items = await listUserCertificates(u.id, { sasMinutes: 60 });
      setPickedTenantCerts(items);
    } catch (e) {
      alert(e?.message || t("certificates.error_generic", "Failed to load certificates"));
    } finally {
      setPickedTenantLoading(false);
    }
  }

  return (
    <div className="max-w-6xl mx-auto px-4 py-6 space-y-5">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold text-slate-900">{t("certificates.tab_mine")}</h1>
        <div className="flex gap-1 border border-slate-200 rounded-xl p-0.5 bg-slate-50">
          {/*<button  --> HIDDEN BECAUSE ORG ADMIN CAN ALREADY SEE FROM EMPLOYEES TAB
            className={`px-4 py-1.5 text-sm font-medium rounded-lg transition-colors ${
              tab === "mine" ? "bg-white text-blue-700 shadow-sm" : "text-slate-500 hover:text-slate-800"
            }`}
            onClick={() => setTab("mine")}
          >
            {t("certificates.tab_mine")}
          </button>
          {canSeeTeam && (
            <button
              className={`px-4 py-1.5 text-sm font-medium rounded-lg transition-colors ${
                tab === "team" ? "bg-white text-blue-700 shadow-sm" : "text-slate-500 hover:text-slate-800"
              }`}
              onClick={() => setTab("team")}
            >
              {t("certificates.tab_team")}
            </button>
          )}
          {canSeeTenant && (
            <button
              className={`px-4 py-1.5 text-sm font-medium rounded-lg transition-colors ${
                tab === "tenant" ? "bg-white text-blue-700 shadow-sm" : "text-slate-500 hover:text-slate-800"
              }`}
              onClick={() => setTab("tenant")}
            >
              {t("certificates.tab_tenant")}
            </button>
          )*/}
        </div>
      </div>

      {/* Mine */}
      {/*tab === "mine" && */(
        <div className="space-y-4">
          {/* Fisa SSM / Fisa SU downloadable files */}
          <div className="flex gap-3">
            <button
              disabled
              className="px-3 py-1.5 rounded-lg text-xs font-medium bg-slate-100 text-slate-400 cursor-not-allowed border border-slate-200"
            >
              {t("docs.ssm_file")}
            </button>
            <button
              disabled
              className="px-3 py-1.5 rounded-lg text-xs font-medium bg-slate-100 text-slate-400 cursor-not-allowed border border-slate-200"
            >
              {t("docs.su_file")}
            </button>
          </div>

          {/* SSM and SU certificate cards */}
          <div className="grid md:grid-cols-2 gap-4">
            <Card title={t("docs.ssm_title")}>
              {myLoading ? (
                <div className="text-sm text-slate-500">{t("certificates.loading")}</div>
              ) : (
                <CertList items={demoTransform(myItems.filter((c) => c.kind === "SSM"), "SSM")} />
              )}
            </Card>
            <Card title={t("docs.su_title")}>
              {myLoading ? (
                <div className="text-sm text-slate-500">{t("certificates.loading")}</div>
              ) : (
                <CertList items={demoTransform(myItems.filter((c) => c.kind === "SU"), "SU")} />
              )}
            </Card>
          </div>
        </div>
      )}

      {/*tab === "team" && */false && canSeeTeam && (
        <div className="grid md:grid-cols-2 gap-4">
          <Card title={t("certificates.team_pick_title")}>
            <div className="mb-3">
              <input
                className="px-3 py-2 rounded-xl border border-slate-200 text-sm outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 w-full bg-white text-slate-700 placeholder:text-slate-400"
                placeholder={t("certificates.search_placeholder")}
                value={qTeam}
                onChange={(e) => setQTeam(e.target.value)}
              />
            </div>
            {reportsLoading ? (
              <div className="text-sm text-slate-500">
                {t("certificates.loading_reports")}
              </div>
            ) : (
              <UserPickerList
                items={filteredReports}
                selectedId={pickedReport?.id || null}
                onPick={loadReportCerts}
                emptyLabel={t("certificates.no_direct_reports")}
              />
            )}
          </Card>

          <Card
            title={
              pickedReport
                ? t("certificates.certs_for", {
                    name: pickedReport.name || pickedReport.email,
                  })
                : t("certificates.certs_generic")
            }
          >
            {pickedReportLoading ? (
              <div className="text-sm text-slate-500">
                {t("certificates.loading")}
              </div>
            ) : pickedReport ? (
              <CertList items={pickedReportCerts} />
            ) : (
              <div className="text-sm text-slate-500">
                {t("certificates.pick_user_prompt")}
              </div>
            )}
          </Card>
        </div>
      )}

      {/*tab === "tenant" && */false && canSeeTenant && (
        <div className="grid md:grid-cols-2 gap-4">
          <Card title={t("certificates.tenant_pick_title")}>
            <div className="mb-3">
              <input
                className="px-3 py-2 rounded-xl border border-slate-200 text-sm outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 w-full bg-white text-slate-700 placeholder:text-slate-400"
                placeholder={t("certificates.search_placeholder")}
                value={qTenant}
                onChange={(e) => setQTenant(e.target.value)}
              />
            </div>
            {tenantLoading ? (
              <div className="text-sm text-slate-500">
                {t("certificates.searching")}
              </div>
            ) : (
              <UserPickerList
                items={tenantUsers}
                selectedId={pickedTenantUser?.id || null}
                onPick={loadTenantUserCerts}
                emptyLabel={t("certificates.no_users")}
              />
            )}
          </Card>

          <Card
            title={
              pickedTenantUser
                ? t("certificates.certs_for", {
                    name: pickedTenantUser.name || pickedTenantUser.email,
                  })
                : t("certificates.certs_generic")
            }
          >
            {pickedTenantLoading ? (
              <div className="text-sm text-slate-500">
                {t("certificates.loading")}
              </div>
            ) : pickedTenantUser ? (
              <CertList items={pickedTenantCerts} />
            ) : (
              <div className="text-sm text-slate-500">
                {t("certificates.pick_user_prompt")}
              </div>
            )}
          </Card>
        </div>
      )}
    </div>
  );
}
